import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, ListChecks, ExternalLink } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "Not Started", bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-600 dark:text-slate-300" },
  { value: "In Development", bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-300" },
  { value: "UAT", bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-300" },
  { value: "Delivered", bg: "bg-green-100 dark:bg-green-900/40", text: "text-green-700 dark:text-green-300" },
  { value: "Blocked", bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-300" },
  { value: "Cancelled", bg: "bg-slate-200 dark:bg-slate-600", text: "text-slate-500 dark:text-slate-400" },
];

const getStatusStyle = (status) => STATUSES.find((s) => s.value === status) || STATUSES[0];

export const TrackingPanel = ({ onNavigate, highlightTarget }) => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [items, setItems] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const cardRefs = useRef({});

  const fetchItems = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const [trackRes, reqRes] = await Promise.all([
        axios.get(`${API}/tracking`, { params: { product_id: productId } }),
        axios.get(`${API}/requirements`, { params: { product_id: productId } }),
      ]);
      setItems(trackRes.data);
      setRequirements(reqRes.data);
    } catch (err) {
      console.error("Failed to fetch tracking:", err);
    }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Auto-scroll to highlighted item
  useEffect(() => {
    if (highlightTarget && cardRefs.current[highlightTarget]) {
      cardRefs.current[highlightTarget].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightTarget, items]);

  const addItem = async () => {
    try {
      const res = await axios.post(`${API}/tracking`, {
        product_id: productId,
        user_story_title: "",
        jira_id: "",
        requirement_id: "",
        status: "Not Started",
      });
      setItems((prev) => [...prev, res.data]);
    } catch (err) {
      console.error("Failed to add tracking item:", err);
    }
  };

  const updateField = (itemId, field, value) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, [field]: value } : i)));
  };

  const saveItem = async (item) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    try {
      await axios.patch(`${API}/tracking/${item.id}`, {
        user_story_title: item.user_story_title,
        jira_id: item.jira_id,
        requirement_id: item.requirement_id,
        status: item.status,
      });
    } catch (err) {
      console.error("Failed to save:", err);
    }
    setSaving((prev) => ({ ...prev, [item.id]: false }));
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/tracking/${itemId}`);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const getReqLabel = (reqId) => {
    const req = requirements.find((r) => r.id === reqId);
    return req ? `REQ-${String(req.req_number).padStart(3, '0')}` : "";
  };

  const filtered = statusFilter ? items.filter((i) => i.status === statusFilter) : items;

  const statusCounts = STATUSES.map((s) => ({
    ...s,
    count: items.filter((i) => i.status === s.value).length,
  }));

  return (
    <>
    <div data-testid="tracking-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-violet-500 dark:text-violet-400 mb-3">Development Progress</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Tracking</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Track user stories linked to requirements through Jira development workflow</p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Status summary badges */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="tracking-status-summary">
        <Badge
          className={`cursor-pointer transition-all ${!statusFilter ? "ring-2 ring-slate-400 dark:ring-slate-500" : ""} bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700`}
          onClick={() => setStatusFilter("")}
        >
          All ({items.length})
        </Badge>
        {statusCounts.filter((s) => s.count > 0).map((s) => (
          <Badge
            key={s.value}
            className={`cursor-pointer transition-all ${statusFilter === s.value ? "ring-2 ring-slate-400 dark:ring-slate-500" : ""} ${s.bg} ${s.text} border hover:opacity-80`}
            onClick={() => setStatusFilter(statusFilter === s.value ? "" : s.value)}
          >
            {s.value} ({s.count})
          </Badge>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          <span className="ml-2 text-sm text-slate-400">Loading tracking items...</span>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tracking-items-list">
          {filtered.map((item) => {
            const st = getStatusStyle(item.status);
            const isHighlighted = highlightTarget === item.id;
            return (
              <Card
                key={item.id}
                ref={(el) => { cardRefs.current[item.id] = el; }}
                className={`card-modern group transition-all duration-500 ${isHighlighted ? "ring-2 ring-violet-500 dark:ring-violet-400 shadow-lg shadow-violet-500/10" : ""}`}
                data-testid={`tracking-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                    {/* US ID badge + User Story Title */}
                    <div className="md:col-span-4">
                      <label className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">User Story</label>
                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 text-[10px] font-bold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 px-2 py-1 rounded-md font-mono">
                          US-{String(item.story_number || 0).padStart(3, '0')}
                        </span>
                        <Input
                          value={item.user_story_title}
                          onChange={(e) => updateField(item.id, "user_story_title", e.target.value)}
                          onBlur={() => saveItem(item)}
                          placeholder="Enter user story title..."
                          className="h-9 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                          data-testid={`tracking-story-${item.id}`}
                        />
                      </div>
                    </div>

                    {/* Jira ID */}
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">Jira ID</label>
                      <div className="flex items-center gap-1">
                        <Input
                          value={item.jira_id}
                          onChange={(e) => updateField(item.id, "jira_id", e.target.value)}
                          onBlur={() => saveItem(item)}
                          placeholder="PROJ-123"
                          className="h-9 text-sm font-mono dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                          data-testid={`tracking-jira-${item.id}`}
                        />
                        {item.jira_id && (
                          <a href={`https://jira.atlassian.com/browse/${item.jira_id}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-blue-500 transition-colors" title="Open in Jira">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Requirement ID — clickable to navigate */}
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">Requirement</label>
                      <Select value={item.requirement_id || "_none"} onValueChange={(val) => { updateField(item.id, "requirement_id", val === "_none" ? "" : val); setTimeout(() => saveItem({ ...item, requirement_id: val === "_none" ? "" : val }), 100); }}>
                        <SelectTrigger className="h-9 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid={`tracking-req-${item.id}`}>
                          <SelectValue placeholder="Link requirement" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                          <SelectItem value="_none" className="dark:text-slate-300">No Requirement</SelectItem>
                          {requirements.map((req) => (
                            <SelectItem key={req.id} value={req.id} className="dark:text-slate-100">
                              REQ-{String(req.req_number).padStart(3, '0')}: {req.title?.substring(0, 30)}{req.title?.length > 30 ? "..." : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status */}
                    <div className="md:col-span-3">
                      <label className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">Status</label>
                      <Select value={item.status} onValueChange={(val) => { updateField(item.id, "status", val); setTimeout(() => saveItem({ ...item, status: val }), 100); }}>
                        <SelectTrigger className={`h-9 text-sm font-semibold ${st.bg} ${st.text} border`} data-testid={`tracking-status-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                          {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value} className="dark:text-slate-100">{s.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Delete */}
                    <div className="md:col-span-1 flex items-end justify-end">
                      <button
                        onClick={() => setDeleteTarget({ id: item.id, label: item.user_story_title })}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 mt-5"
                        title="Delete"
                        data-testid={`tracking-delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Linked requirement badge — clickable */}
                  {item.requirement_id && (
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700 flex items-center gap-2">
                      <button
                        onClick={() => onNavigate && onNavigate("tobe-enhancements", item.requirement_id)}
                        className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                        title={`Go to ${getReqLabel(item.requirement_id)}`}
                        data-testid={`tracking-goto-req-${item.id}`}
                      >
                        <Badge className="bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800 text-[10px] hover:bg-violet-100 dark:hover:bg-violet-900/50 cursor-pointer">
                          {getReqLabel(item.requirement_id)}
                        </Badge>
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {filtered.length === 0 && !loading && (
            <div className="text-center py-16">
              <ListChecks className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 dark:text-slate-500 text-sm">
                {statusFilter ? "No tracking items match this filter" : "No tracking items yet. Add one to start tracking development."}
              </p>
            </div>
          )}

          {/* Add button */}
          <Button
            onClick={addItem}
            className="bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-slate-700 dark:hover:bg-slate-600 shadow-sm text-xs h-9 px-4"
            data-testid="tracking-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Tracking Item
          </Button>
        </div>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteItem(deleteTarget?.id)} title="Delete User Story" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};